import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Plus, Search, X, ChevronRight, Copy, ShieldCheck, Info, Loader2, Trash2, Paperclip, FileCheck2, Mail, Phone, RefreshCw, ServerCog, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader.jsx';
import { Card, Empty } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { sdx500Api, SIGNUP_BASE } from '../api/sdx500.js';
import { overlayDismiss } from '../lib/overlayDismiss';

/* ── 生命週期狀態（分界 = provision 來了沒） ── */
const STATUS = {
    awaiting_provision: { label: '待 SDX500 開通', dot: 'bg-warn-500', pill: 'bg-warn-100 text-[#8a5e16]' },
    provisioned: { label: '待客戶註冊', dot: 'bg-accent-500', pill: 'bg-accent-50 text-accent-700' },
    active: { label: '使用中', dot: 'bg-success-500', pill: 'bg-success-100 text-[#3f5733]' },
    suspended: { label: '已停用', dot: 'bg-danger-500', pill: 'bg-danger-100 text-[#7a2c27]' },
};
const STATUS_ORDER = ['awaiting_provision', 'provisioned', 'active', 'suspended'];
// 尚未真正啟用（沒建客戶帳號/租戶）→ 可直接刪除；active/suspended 要走拆除流程
const DELETABLE_STATUSES = ['awaiting_provision', 'provisioned'];

function LicensePills({ list }) {
    if (!list || !list.length) return <span className="text-ink-300 text-xs">—</span>;
    return (
        <span className="inline-flex flex-wrap gap-1.5">
            {list.map((l, i) => (
                <span
                    key={i}
                    className={[
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium font-mono border',
                        l.productType === 'CS'
                            ? 'bg-accent-50 text-accent-700 border-accent-100'
                            : 'bg-cream-100 text-ink-700 border-cream-300',
                    ].join(' ')}
                >
                    {l.productType} · {l.seats}
                </span>
            ))}
        </span>
    );
}

function StatusPill({ status }) {
    const s = STATUS[status] || STATUS.awaiting_provision;
    return (
        <span className={['inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap', s.pill].join(' ')}>
            <span className={['size-1.5 rounded-full', s.dot].join(' ')} />
            {s.label}
        </span>
    );
}

// region → IANA 時區。東訊授權以「設備所在地當地午夜」為界、存成 UTC（如台灣 00:00 = 前一天 16:00Z）
// → 顯示時要轉回當地時區，否則跨午夜的日期會少一天。
const REGION_TZ = {
    tw: 'Asia/Taipei',
    th: 'Asia/Bangkok',
    jp: 'Asia/Tokyo',
    hk: 'Asia/Hong_Kong',
    cn: 'Asia/Shanghai',
};
function tzForRegion(region) {
    return REGION_TZ[String(region || '').toLowerCase()] || 'Asia/Taipei';   // SDX500 皆東訊台灣 → 預設台北
}

// 把 UTC ISO 時間依地理位置(region)轉當地日期 YYYY-MM-DD。純日期字串(無時間)則原樣切。
function fmtDate(s, region) {
    if (!s) return '—';
    const str = String(s);
    // 已是純日期(YYYY-MM-DD，無 T/Z) → 不需轉時區
    if (!/[TZ]/.test(str)) return str.slice(0, 10);
    const d = new Date(str);
    if (isNaN(d.getTime())) return str.slice(0, 10);
    // en-CA 給 YYYY-MM-DD 格式
    return d.toLocaleDateString('en-CA', { timeZone: tzForRegion(region) });
}

export default function Sdx500LicensePage() {
    const [items, setItems] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [distributorFilter, setDistributorFilter] = useState('');   // 大盤商 uid
    const [appOnly, setAppOnly] = useState(false);                     // 只看有待處理申請
    const [expanded, setExpanded] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    async function load({ soft = false } = {}) {
        if (!soft) setLoading(true);
        setError(null);
        try {
            const [lic, dl] = await Promise.all([sdx500Api.listLicenses(), sdx500Api.dealers()]);
            setItems(lic.items || []);
            setDealers(dl.dealers || []);
        } catch (e) {
            setError(e);
        } finally {
            if (!soft) setLoading(false);
        }
    }
    // soft reload：不觸發 loading spinner → 展開列不收合、子元件 state(如 API-6 面板)保留
    const softReload = () => load({ soft: true });
    useEffect(() => { load(); }, []);

    const counts = useMemo(() => {
        const c = { awaiting_provision: 0, provisioned: 0, active: 0, suspended: 0 };
        for (const it of items) c[it.status] = (c[it.status] || 0) + 1;
        return c;
    }, [items]);

    const filtered = useMemo(() => {
        let r = items;
        if (statusFilter) r = r.filter((i) => i.status === statusFilter);
        if (distributorFilter) r = r.filter((i) => i.distributorUid === distributorFilter);
        if (appOnly) r = r.filter((i) => i.pendingRenewal || i.pendingAddon);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            // 多條件搜尋：MAC / 大盤商 / 公司名 / 客戶 email / 統編 / hostId
            r = r.filter((i) => [
                i.macAddress, i.distributorName, i.distributorEmail,
                i.company, i.ownerEmail, i.taxId, i.hostId,
            ].some((v) => String(v || '').toLowerCase().includes(q)));
        }
        return r;
    }, [items, search, statusFilter, distributorFilter, appOnly]);

    const hasFilter = statusFilter || distributorFilter || appOnly || search.trim();
    const clearFilters = () => { setStatusFilter(''); setDistributorFilter(''); setAppOnly(false); setSearch(''); };

    async function suspend(mac) {
        if (!confirm(`確定停用 ${mac}？分機會反註冊。`)) return;
        try { await sdx500Api.suspend(mac); await load(); }
        catch (e) { alert(`停用失敗：${e.message}`); }
    }

    async function remove(mac) {
        if (!confirm(`確定刪除授權 ${mac}？\n此筆尚未啟用（未建客戶帳號），刪除後若已產生啟用連結也會一併失效。`)) return;
        try { await sdx500Api.remove(mac); await load(); }
        catch (e) { alert(`刪除失敗：${e.body?.hint || e.message}`); }
    }

    return (
        <div>
            <PageHeader
                eyebrow="QubbyAI × TECOM · partner provisioning"
                title="東訊設備授權管理"
                description="匯入設備授權（MAC + 經銷商歸屬）。SDX500 開通後自動產生客戶啟用連結，客戶註冊即啟用租戶並上線分機。"
                actions={
                    <div className="flex items-center gap-2">
                        <a href="/sdx500-distributors" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium text-ink-700 border border-cream-300 bg-white hover:bg-cream-50 transition">
                            <ShieldCheck size={15} /> 大盤商總覽
                        </a>
                        <Button variant="accent" onClick={() => setDrawerOpen(true)}>
                            <Plus size={15} /> 匯入授權
                        </Button>
                    </div>
                }
            />

            {/* 客戶送來的續約/加購申請 */}
            <PendingApplications />

            {/* stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {STATUS_ORDER.map((k) => (
                    <button
                        key={k}
                        onClick={() => setStatusFilter((cur) => (cur === k ? '' : k))}
                        className={[
                            'text-left bg-white border rounded-lg p-4 transition',
                            statusFilter === k ? 'border-accent-500 ring-2 ring-accent-500/15' : 'border-cream-200 hover:border-cream-400',
                        ].join(' ')}
                    >
                        <div className="heading-display text-2xl font-semibold text-ink-900 leading-none">{counts[k] || 0}</div>
                        <div className="text-xs text-ink-400 mt-2 flex items-center gap-1.5">
                            <span className={['size-1.5 rounded-full', STATUS[k].dot].join(' ')} />
                            {STATUS[k].label}
                        </div>
                    </button>
                ))}
            </div>

            {/* toolbar：搜尋條件（文字 + 大盤商 + 狀態 + 只看有申請）*/}
            <div className="flex items-center flex-wrap gap-2.5 mb-5">
                <div className="relative flex-1 min-w-[240px] max-w-sm">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                    <input
                        type="text"
                        placeholder="搜尋 MAC / 公司名 / 客戶 Email / 統編…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                    />
                </div>
                {/* 大盤商篩選 */}
                <select
                    value={distributorFilter}
                    onChange={(e) => setDistributorFilter(e.target.value)}
                    className="py-2 px-3 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
                    title="依大盤商篩選"
                >
                    <option value="">全部大盤商</option>
                    {dealers.map((d) => (
                        <option key={d.uid} value={d.uid}>{d.displayName || d.email.replace('@tecom.com', '')}</option>
                    ))}
                </select>
                {/* 狀態篩選 */}
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="py-2 px-3 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500"
                    title="依狀態篩選"
                >
                    <option value="">全部狀態</option>
                    {STATUS_ORDER.map((k) => <option key={k} value={k}>{STATUS[k].label}</option>)}
                </select>
                {/* 只看有待處理申請 */}
                <button
                    onClick={() => setAppOnly((v) => !v)}
                    className={['inline-flex items-center gap-1.5 py-2 px-3 text-sm rounded-md border transition',
                        appOnly ? 'bg-warn-100 text-[#8a5e16] border-[#e7cf9a]' : 'bg-white text-ink-600 border-cream-200 hover:bg-cream-50'].join(' ')}
                    title="只看有待處理續約/加購申請的"
                >
                    🔔 有申請
                </button>
                {hasFilter && (
                    <button onClick={clearFilters} className="text-xs text-ink-400 hover:text-ink-700 mono-caption">
                        清除條件 ✕
                    </button>
                )}
                <div className="flex-1" />
                <span className="text-xs text-ink-400 mono-caption">{filtered.length} / {items.length}</span>
            </div>

            {error && (
                <Card className="bg-danger-100/40 border-danger-500/30 mb-4">
                    <div className="text-sm text-danger-500">{error.message}</div>
                </Card>
            )}

            {loading ? (
                <Card><div className="text-sm text-ink-400 italic text-center py-10 flex items-center justify-center gap-2"><Loader2 size={15} className="animate-spin" />載入中…</div></Card>
            ) : filtered.length === 0 ? (
                <Card>
                    <Empty
                        icon={ShieldCheck}
                        title={items.length === 0 ? '尚無授權' : '查無符合'}
                        description={items.length === 0 ? '點右上「匯入授權」登錄第一台 SDX500：填 MAC、選經銷商。' : '換個搜尋或清除篩選。'}
                        action={items.length === 0 ? <Button variant="accent" onClick={() => setDrawerOpen(true)}><Plus size={15} /> 匯入授權</Button> : null}
                    />
                </Card>
            ) : (
                <Card padded={false}>
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-ink-400 font-mono border-b border-cream-200 bg-cream-50">
                                <th className="w-8" />
                                <th className="text-left px-4 py-3 font-medium">MAC Address</th>
                                <th className="text-left px-4 py-3 font-medium">經銷商</th>
                                <th className="text-left px-4 py-3 font-medium">授權</th>
                                <th className="text-left px-4 py-3 font-medium">期限</th>
                                <th className="text-left px-4 py-3 font-medium">狀態</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((row) => (
                                <LicenseRow
                                    key={row.macIdNorm}
                                    row={row}
                                    expanded={expanded === row.macIdNorm}
                                    onToggle={() => setExpanded((c) => (c === row.macIdNorm ? null : row.macIdNorm))}
                                    onSuspend={() => suspend(row.macAddress)}
                                    onDelete={() => remove(row.macAddress)}
                                    onReload={softReload}
                                />
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            {drawerOpen && (
                <ImportDrawer
                    dealers={dealers}
                    onClose={() => setDrawerOpen(false)}
                    onDone={async () => { setDrawerOpen(false); await load(); }}
                />
            )}
        </div>
    );
}

// 客戶端送來的續約/加購申請 — license_admin 看到 → 處理（上傳 .dat）後標記已完成/未通過
function PendingApplications() {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    async function load() {
        setLoading(true);
        try { setApps((await sdx500Api.listApplications('pending')).items || []); }
        catch { setApps([]); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function resolve(id, status) {
        const label = status === 'completed' ? '標記為已處理（授權已更新）' : '婉拒此申請';
        if (!confirm(`確定${label}？`)) return;
        let note = '';
        if (status === 'rejected') { note = prompt('婉拒原因（選填）') || ''; }
        try { await sdx500Api.resolveApplication(id, status, note); await load(); }
        catch (e) { alert(`失敗：${e.body?.hint || e.message}`); }
    }

    if (loading || apps.length === 0) return null;
    return (
        <div className="mb-6 border border-[#e7cf9a] bg-warn-100/60 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <Info size={16} className="text-[#8a5e16]" />
                <h3 className="text-[13px] font-semibold text-[#7a5512]">客戶續約 / 加購申請（{apps.length} 筆待處理）</h3>
            </div>
            <div className="space-y-2">
                {apps.map((a) => {
                    const desc = a.type === 'renewal'
                        ? `續約 ${a.renewYears} 年`
                        : `加購 ${[a.addonAA ? `AA ${a.addonAA} 席` : '', a.addonCS ? `CS ${a.addonCS} 席` : ''].filter(Boolean).join('、')}`;
                    return (
                        <div key={a.id} className="flex items-center gap-3 bg-white border border-cream-200 rounded-lg px-3.5 py-2.5">
                            <span className="text-[13px] font-medium text-ink-900">{a.company || '—'}</span>
                            <span className="text-[13px] text-accent-700">{desc}</span>
                            {a.note && <span className="text-[12px] text-ink-400">「{a.note}」</span>}
                            <span className="font-mono text-[11px] text-ink-300">{a.macIdNorm}</span>
                            <span className="font-mono text-[11px] text-ink-300 flex-1 truncate">{a.requestedByEmail}</span>
                            <button onClick={() => resolve(a.id, 'completed')}
                                className="inline-flex items-center gap-1 text-[12px] text-success-700 border border-success-500/30 bg-white rounded px-2.5 py-1 hover:bg-success-50">
                                <CheckCircle2 size={13} /> 已處理
                            </button>
                            <button onClick={() => resolve(a.id, 'rejected')}
                                className="inline-flex items-center gap-1 text-[12px] text-ink-500 border border-cream-300 bg-white rounded px-2.5 py-1 hover:bg-cream-50">
                                <X size={13} /> 婉拒
                            </button>
                        </div>
                    );
                })}
            </div>
            <p className="text-[11.5px] text-[#8a5e16] mt-2.5">處理方式：向東訊取得對應 .dat → 到該 MAC 授權按「續約 / 加購（上傳 .dat）」→ 回這裡按「已處理」。</p>
        </div>
    );
}

function LicenseRow({ row, expanded, onToggle, onSuspend, onDelete, onReload }) {
    const canDelete = DELETABLE_STATUSES.includes(row.status);
    const hasApi = row.status === 'active' || row.status === 'provisioned';
    const canMigrate = row.status === 'active' || row.status === 'provisioned';   // 設備更換：換 MAC
    const [refreshing, setRefreshing] = useState(false);
    const [remote, setRemote] = useState(null);
    const [remoteErr, setRemoteErr] = useState(null);
    const [busy, setBusy] = useState(false);          // API-4 / API-5 進行中
    const [opMsg, setOpMsg] = useState(null);         // { ok, text } 操作結果
    const [editSeats, setEditSeats] = useState(null); // { AA, CS } 修正預登錄席次的編輯狀態；null = 未展開
    // 上傳 .dat 後設備要一點時間才套用，後端會在背景每分鐘回查一次（共三次）。
    // 後端更新的是資料，畫面不會自己變 → 這裡跟著排幾次 reload，操作者才看得到結果。
    const [autoReloadAt, setAutoReloadAt] = useState(null);   // 觸發時間戳；null = 不排
    // 計時器的 callback 抓到的是排程當下的 row（閉包），拿不到 reload 後的新值 → 用 ref 拿最新的
    const rowRef = useRef(row);
    rowRef.current = row;

    useEffect(() => {
        if (!autoReloadAt) return;
        // 後端節奏是 60/120/180 秒，各晚 10 秒再拉，確保它已經寫完
        const snapshot = JSON.stringify(rowRef.current.license || []);
        const timers = [];
        [70, 130, 195].forEach((sec, i) => timers.push(setTimeout(async () => {
            try { await onReload(); } catch { /* 拉不到就算了，下一次還會再拉 */ }
            // reload 後父層 setState → 重新 render 才會更新 rowRef；等下一個 macrotask 再比才拿得到新值
            timers.push(setTimeout(() => {
                if (JSON.stringify(rowRef.current.license || []) !== snapshot) {
                    timers.forEach(clearTimeout);              // 已經拿到新授權 → 剩下的不用再拉
                    setOpMsg({ ok: true, text: '授權已更新' });
                } else if (i === 2) {
                    // 三次都沒變才說沒變；review 抓到：原本第三次一律顯示「仍無變化」，就算第一次就更新了也照講
                    setOpMsg((m) => (m && m.pending ? { ok: true, text: '自動回查結束；仍無變化請按「從東訊查授權」' } : m));
                }
            }, 0));
        }, sec * 1000)));
        return () => timers.forEach(clearTimeout);   // 換頁 / 收合就取消，避免對已卸載的元件動手
    }, [autoReloadAt]);   // eslint-disable-line react-hooks/exhaustive-deps

    // 修正預登錄席次：保留原本各筆的日期，只換總席數（AA / CS 各一筆；原本沒有的型別不補）
    async function saveExpectedSeats() {
        const cur = row.expectedLicenses || [];
        const pick = (k) => cur.find((l) => (l.productType === 'CS' ? 'CS' : 'AA') === k) || {};
        const next = ['AA', 'CS']
            .map((k) => ({ productType: k, seats: Number(editSeats[k]) || 0, licenseStart: pick(k).licenseStart || null, licenseEnd: pick(k).licenseEnd || null }))
            .filter((l) => l.seats > 0);
        if (!next.length) { setOpMsg({ ok: false, text: '至少要有一種產品席次 > 0' }); return; }
        setBusy(true); setOpMsg(null);
        try {
            const r = await sdx500Api.setExpectedLicenses(row.macAddress, next);
            setOpMsg({
                ok: true,
                text: `預登錄席次已更新為 ${next.map((l) => `${l.productType} ${l.seats} 席`).join('、')}`
                    + (r.stillMismatchWithLastUpload === true ? '；⚠️ 仍與設備上次上傳的席次不同，設備再按開通仍會被拒'
                        : r.stillMismatchWithLastUpload === false ? '；與設備上次上傳一致，請東訊端再按一次「開通鏈接」' : ''),
            });
            setEditSeats(null);
            onReload && onReload();
        } catch (e) {
            setOpMsg({ ok: false, text: `更新失敗：${e.body?.hint || e.body?.error || e.message}` });
        } finally {
            setBusy(false);
        }
    }
    const [testing, setTesting] = useState(false);    // 連線測試進行中
    const [connTest, setConnTest] = useState(null);   // { ok, text } 連線測試結果
    // 從東訊查授權 = 把東訊當權威**同步下來**（覆寫本地 + 席次），再 reload 讓授權明細/摘要跟著更新
    async function refreshFromTecom(ev) {
        ev.stopPropagation();
        setRefreshing(true); setRemoteErr(null);
        try {
            const r = await sdx500Api.licenseRefresh(row.macAddress);
            setRemote(r);
            await onReload();   // 一律重載 → 授權明細/摘要立即反映最新（含加購新 Id）
        }
        catch (e) { setRemoteErr(e.body?.hint || e.body?.error || e.message); }
        finally { setRefreshing(false); }
    }

    // 測試東訊連線（API-2 登入 → API-3 取 SIP 設定）：驗證設備 API 帳號健康
    async function testConnection(ev) {
        ev.stopPropagation();
        setTesting(true); setConnTest(null);
        try {
            const r = await sdx500Api.sipConfigTest(row.macAddress);
            setConnTest({ ok: true, text: `連線正常 — PBX ${r.pbx?.host}:${r.pbx?.port}/${r.pbx?.transport}，${r.seatCount} 支分機` });
        } catch (e) {
            const code = e.body?.code;
            const detail = e.body?.hint || e.body?.error || e.message;
            setConnTest({
                ok: false,
                text: code === 'SDX500_AUTH_REJECTED'
                    ? `設備拒絕登入（帳號密碼錯）— 此台 SDX500 的 qubby API 帳號未建立或密碼不符，請東訊檢查設備端設定`
                    : `連線失敗：${detail}`,
            });
        } finally {
            setTesting(false);
        }
    }

    // 續約 / 升級 / 加購：上傳新 .dat → 東訊 API-4 → API-6 回查 → 同步席次
    async function onRenewPick(ev) {
        const f = ev.target.files?.[0];
        ev.target.value = '';
        if (!f) return;
        if (!confirm(`把「${f.name}」傳給東訊 SDX500（${row.api?.host || '—'}）進行續約/升級/加購？\n成功後會自動回查授權並同步席次。`)) return;
        setBusy(true); setOpMsg(null);
        try {
            const content = await f.text();
            const r = await sdx500Api.licenseUpdate(row.macAddress, { filename: f.name, content });
            // pendingRefresh = 設備收下了但還沒套用（0909 實測：上傳當下回查拿到的仍是舊授權）。
            // 這時後端已排了背景回查（每分鐘一次、共三次），不能顯示「授權無變化」——
            // 那會讓人以為檔案傳錯又按一次。
            setOpMsg({
                ok: true,
                pending: !!r.pendingRefresh,
                text: !r.refreshed
                    ? '已通知東訊；回查失敗，稍後請按「從東訊查授權」對照'
                    : r.pendingRefresh
                        ? '已通知東訊；設備尚未套用，3 分鐘內自動回查'
                        : `已通知東訊，授權已更新${r.seatsSynced ? '，席次已同步' : ''}`,
            });
            if (r.pendingRefresh) setAutoReloadAt(Date.now());   // 排 3 次自動 reload，跟上後端的背景回查
            await onReload();
        } catch (e) {
            setOpMsg({ ok: false, text: `續約失敗：${e.body?.hint || e.body?.error || e.message}` });
        } finally {
            setBusy(false);
        }
    }

    // End-user 遺失帳密：核對公司/電話後，補發密碼重設連結 email 給客戶本人
    async function resendLogin(ev) {
        ev.stopPropagation();
        if (!confirm(`補發登入密碼給客戶？\n\n公司：${row.company || '—'}\nEmail：${row.ownerEmail || '—'}\n電話：${row.ownerPhone || '—'}\n\n請先與客戶核對公司名稱／電話確認身分。確認後會寄一封「密碼重設連結」到上面的 Email，客戶點連結自行設定新密碼。`)) return;
        setBusy(true); setOpMsg(null);
        try {
            const r = await sdx500Api.resendLogin(row.macAddress);
            setOpMsg({ ok: true, text: `已寄出密碼重設連結到 ${r.email}，請客戶收信（含垃圾匣）點連結設定新密碼。` });
        } catch (e) {
            setOpMsg({ ok: false, text: `補發失敗：${e.body?.hint || e.body?.error || e.message}` });
        } finally {
            setBusy(false);
        }
    }

    // 設備更換 / 更換 MAC（換板 CCB）：把授權搬到新 MAC，帳號/租戶/角色/記錄全留
    async function migrateMac(ev) {
        ev.stopPropagation();
        const input = prompt(`設備更換（換板 CCB）— 把此授權搬到新 MAC。\n舊 MAC：${row.macAddress}\n\n請輸入新 MAC（12 碼 hex，可含 : - 分隔）：`);
        if (!input) return;
        if (!/^[0-9a-fA-F]{12}$/.test(input.replace(/[^0-9a-fA-F]/g, ''))) { alert('新 MAC 格式須為 12 碼 hex'); return; }
        if (!confirm(`確定把授權從\n  ${row.macAddress}\n搬到\n  ${input}\n？\n\n客戶帳號、租戶、AI 角色、知識庫、通話記錄、授權全部保留，只換 MAC。舊 MAC 標記為「已更換」。\n\n⚠️ 東訊側仍須把 license 重發到新 MAC（到期日對齊）。`)) return;
        setBusy(true); setOpMsg(null);
        try {
            const r = await sdx500Api.migrateMac(row.macAddress, input);
            setOpMsg({ ok: true, text: `已更換：${r.oldMac} → ${r.newMac}（帳號指標已更新${r.accountRepointed ? '' : '失敗'}）。租戶 ${r.hostId} 及所有資料保留。` });
            await onReload();
        } catch (e) {
            setOpMsg({ ok: false, text: `更換失敗：${e.body?.hint || e.body?.error || e.message}` });
        } finally {
            setBusy(false);
        }
    }

    // 作廢一組授權（API-5，依 Id）。未到期後端擋 409 → 二次確認帶 force
    async function revokeOne(ev, l) {
        ev.stopPropagation();
        if (!confirm(`確定向東訊作廢 Id ${l.Id}（${l.productType} · ${l.seats} 席）？\n東訊商務規則僅新購無轉換，作廢後原服務不退款。`)) return;
        setBusy(true); setOpMsg(null);
        try {
            let r;
            try {
                r = await sdx500Api.revokeLicense(row.macAddress, l.Id);
            } catch (e) {
                if (e.body?.error === 'license_not_expired') {
                    if (!confirm(`⚠️ 此組授權 ${fmtDate(l.licenseEnd, row.region)} 才到期，東訊不建議刪除未到期服務。\n仍要強制作廢？`)) { setBusy(false); return; }
                    r = await sdx500Api.revokeLicense(row.macAddress, l.Id, true);
                } else throw e;
            }
            setOpMsg({
                ok: true,
                text: `已作廢 Id ${l.Id}${r.seatsSynced ? '，席次已同步' : ''}${r.suspended ? '；已無有效授權 → 整筆停用' + (r.phonesUnregistered ? '，分機已反註冊' : '（分機反註冊未完成，請檢查）') : ''}`,
            });
            await onReload();
        } catch (e) {
            setOpMsg({ ok: false, text: `作廢失敗：${e.body?.hint || e.body?.error || e.message}` });
        } finally {
            setBusy(false);
        }
    }
    const lic = (row.license && row.license.length ? row.license : row.expectedLicenses) || [];
    const term = lic[0] ? `${fmtDate(lic[0].licenseStart, row.region)} → ${fmtDate(lic[0].licenseEnd, row.region)}` : '—';
    const signupUrl = row.signupToken ? `${SIGNUP_BASE}?token=${row.signupToken}` : null;
    return (
        <>
            <tr className="border-b border-cream-100 hover:bg-cream-50 cursor-pointer" onClick={onToggle}>
                <td className="pl-4">
                    <ChevronRight size={15} className={['text-ink-300 transition', expanded ? 'rotate-90' : ''].join(' ')} />
                </td>
                <td className="px-4 py-4">
                    <span className="font-mono text-[13px] font-medium text-ink-900">{row.macAddress}</span>
                    {(row.pendingRenewal || row.pendingAddon) && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10.5px] font-medium bg-warn-100 text-[#8a5e16] align-middle" title="有待處理的續約/加購申請">
                            🔔 {[row.pendingRenewal && '續約', row.pendingAddon && '加購'].filter(Boolean).join('/')}申請
                        </span>
                    )}
                </td>
                <td className="px-4 py-4">
                    <div className="text-ink-700 text-sm">{row.distributorName || row.distributorEmail?.replace('@tecom.com', '') || '—'}</div>
                    <div className="font-mono text-[10px] text-ink-300">{row.distributorEmail}</div>
                </td>
                <td className="px-4 py-4"><LicensePills list={lic} /></td>
                <td className="px-4 py-4"><span className="font-mono text-[11.5px] text-ink-500">{term}</span></td>
                <td className="px-4 py-4"><StatusPill status={row.status} /></td>
            </tr>
            {expanded && (
                <tr className="bg-cream-50 border-b border-cream-100">
                    <td colSpan={6} className="px-6 pb-6 pt-1">
                        <div className="grid md:grid-cols-2 gap-7">
                            <div>
                                <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-3 mt-3">
                                    授權明細{row.license?.length ? '（Id 來自 SDX500）' : '（預登錄・等 provision 對齊 Id）'}
                                </h4>
                                {lic.map((l, i) => (
                                    <div key={i} className="flex items-center gap-3 py-2 border-b border-dashed border-cream-300 last:border-0">
                                        <span className="font-mono text-[11px] text-ink-400 bg-white border border-cream-200 rounded px-1.5 py-0.5">
                                            {l.Id != null ? `Id ${l.Id}` : '—'}
                                        </span>
                                        <span className="text-[13px] text-ink-700 flex-1"><b>{l.productType}</b> · {l.seats} 席</span>
                                        <span className="font-mono text-[11px] text-ink-400">{fmtDate(l.licenseStart, row.region)} → {fmtDate(l.licenseEnd, row.region)}</span>
                                        {hasApi && l.Id != null && row.license?.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={(ev) => revokeOne(ev, l)}
                                                disabled={busy}
                                                title="向東訊作廢這組授權（API-5）"
                                                className="text-[11px] text-ink-300 hover:text-danger-500 border border-cream-300 hover:border-danger-500/40 rounded px-1.5 py-0.5 transition disabled:opacity-40"
                                            >
                                                作廢
                                            </button>
                                        )}
                                    </div>
                                ))}
                                {row.lastProvisionRejection && (
                                    <div className="mt-3 flex items-start gap-2 text-[12px] text-danger-600 bg-danger-100/40 border border-danger-500/30 rounded-lg px-3 py-2">
                                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                        <div>
                                            <div className="font-medium">設備上次按「開通鏈接」被拒絕（{fmtDate(row.lastProvisionRejection.at, row.region)}）</div>
                                            <div className="whitespace-pre-line mt-0.5">{row.lastProvisionRejection.message}</div>
                                            <div className="text-[11px] text-ink-400 mt-1">錯在預登錄 → 下方「修正預登錄席次」；錯在設備 → 請東訊端修正授權檔後再按開通。</div>
                                        </div>
                                    </div>
                                )}
                                {row.licenseMismatch && !row.lastProvisionRejection && (
                                    <div className="mt-3 flex items-start gap-2 text-[12px] text-[#8a5e16] bg-warn-100 border border-[#e7cf9a] rounded-lg px-3 py-2">
                                        <Info size={14} className="mt-0.5 shrink-0" />
                                        provision 帶來的席次與預登錄不一致（以 SDX500 為準，已記旗標）
                                    </div>
                                )}
                                {/* 修正預登錄席次：任何狀態都可改（含已開通）。開通改為席次不符即拒絕後，錯在預登錄那邊的唯一出路 */}
                                <div className="mt-3" onClick={(ev) => ev.stopPropagation()}>
                                    {editSeats ? (
                                        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                                            <span className="text-ink-500">預登錄席次</span>
                                            {['AA', 'CS'].map((k) => (
                                                <label key={k} className="inline-flex items-center gap-1">
                                                    <span className="font-mono text-[11px] text-ink-400">{k}</span>
                                                    <input
                                                        type="number" min="0" step="1"
                                                        value={editSeats[k]}
                                                        onChange={(e) => setEditSeats((s) => ({ ...s, [k]: e.target.value }))}
                                                        className="w-16 px-2 py-1 rounded-md border border-cream-300 bg-white font-mono text-[12.5px]"
                                                    />
                                                    <span className="text-ink-400">席</span>
                                                </label>
                                            ))}
                                            <button
                                                type="button" disabled={busy} onClick={saveExpectedSeats}
                                                className="px-2.5 py-1 rounded-md text-[12px] font-medium text-white bg-accent-500 hover:bg-accent-600 disabled:opacity-50"
                                            >
                                                {busy ? <Loader2 size={13} className="animate-spin inline" /> : '儲存'}
                                            </button>
                                            <button type="button" disabled={busy} onClick={() => setEditSeats(null)} className="px-2 py-1 rounded-md text-[12px] text-ink-500 hover:text-ink-700">取消</button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const sum = { AA: 0, CS: 0 };
                                                for (const l of row.expectedLicenses || []) sum[l.productType === 'CS' ? 'CS' : 'AA'] += Number(l.seats) || 0;
                                                setEditSeats(sum);
                                            }}
                                            title="修改 Qubby 這邊登錄的席次（不通知東訊、不改設備）；改完請東訊端再按一次「開通鏈接」"
                                            className="text-[12px] text-ink-500 hover:text-accent-700 underline underline-offset-2"
                                        >
                                            修正預登錄席次
                                        </button>
                                    )}
                                </div>
                                {Array.isArray(row.endpoints) && row.endpoints.length > 0 && (
                                    <>
                                        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 mt-4">PBX / 分機</h4>
                                        <div className="font-mono text-[11.5px] text-ink-500 mb-2">
                                            {row.pbx ? `${row.pbx.publicIp}:${row.pbx.ipPhoneRegisterPort} / ${row.pbx.transport}` : '—'}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {row.endpoints.map((e, i) => (
                                                <span key={i} className="font-mono text-[11px] text-ink-700 bg-white border border-cream-200 rounded px-2 py-1">
                                                    {e.extension} <span className="text-ink-300">{e.productType}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {Array.isArray(row.datFiles) && row.datFiles.length > 0 && (
                                    <>
                                        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 mt-4">授權檔（檔名＝授權碼）</h4>
                                        <div className="space-y-1">
                                            {row.datFiles.map((f, i) => (
                                                <div key={i} className="font-mono text-[11.5px] text-ink-700 flex items-center gap-1.5">
                                                    <FileCheck2 size={12} className="text-success-500 shrink-0" /> {f.filename}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div>
                                {row.status === 'provisioned' && signupUrl && (
                                    <>
                                        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 mt-3">客戶啟用連結（一次性）</h4>
                                        <div className="bg-white border border-cream-200 rounded-lg p-4">
                                            <div className="flex items-center gap-2">
                                                <code className="flex-1 font-mono text-[11px] text-accent-700 bg-accent-50 border border-accent-100 rounded px-2 py-1.5 truncate">{signupUrl}</code>
                                                <button
                                                    className="border border-cream-300 bg-white rounded px-2 py-1.5 text-ink-500 hover:bg-cream-50"
                                                    onClick={(ev) => { ev.stopPropagation(); navigator.clipboard?.writeText(signupUrl); }}
                                                    title="複製"
                                                >
                                                    <Copy size={14} />
                                                </button>
                                            </div>
                                            <div className="text-[11.5px] text-ink-400 mt-2">效期至 {fmtDate(row.signupExpiresAt, row.region)} · 客戶提交後立即失效</div>
                                        </div>
                                    </>
                                )}
                                {row.status === 'active' && (
                                    <>
                                        <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2 mt-3">客戶帳號</h4>
                                        <div className="bg-white border border-cream-200 rounded-lg p-4 text-[13px] space-y-1.5">
                                            {row.company && <div className="text-ink-900 font-medium">{row.company}</div>}
                                            <div className="flex items-center gap-2 text-ink-700">
                                                <Mail size={13} className="text-ink-400 shrink-0" />
                                                <span className="truncate">{row.ownerEmail || '—'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-ink-700">
                                                <Phone size={13} className="text-ink-400 shrink-0" />
                                                <span>{row.ownerPhone || '—'}</span>
                                            </div>
                                            <div className="pt-1.5 mt-1.5 border-t border-cream-100 text-ink-400 text-[11.5px]">
                                                <span className="font-mono">{row.hostId}</span> · 經銷商 {row.distributorName || row.distributorEmail?.replace('@tecom.com', '')} 開立
                                            </div>
                                            {/* End-user 遺失帳密 → 核對公司/電話後補發密碼重設連結 */}
                                            <div className="pt-2 mt-1" onClick={(ev) => ev.stopPropagation()}>
                                                <button
                                                    type="button"
                                                    onClick={resendLogin}
                                                    disabled={busy}
                                                    title="客戶忘記密碼：寄一封密碼重設連結到客戶 Email"
                                                    className="inline-flex items-center gap-1.5 text-[12px] text-accent-700 border border-accent-500/30 bg-white rounded px-2.5 py-1.5 hover:bg-accent-50 transition disabled:opacity-50"
                                                >
                                                    <Mail size={13} /> 補發登入密碼（寄重設信）
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {row.status === 'awaiting_provision' && (
                                    <div className="mt-3 flex items-start gap-2 text-[12px] text-[#8a5e16] bg-warn-100 border border-[#e7cf9a] rounded-lg px-3 py-2.5">
                                        <Info size={14} className="mt-0.5 shrink-0" />
                                        已預登錄，尚未收到 SDX500 的 provision。經銷商於 SDX500 點「開通」後，本筆會自動帶入分機/PBX 並產生啟用連結。
                                    </div>
                                )}
                                {hasApi && (
                                    <>
                                        <div className="flex items-center justify-between mt-4 mb-2">
                                            <h4 className="font-mono text-[10px] uppercase tracking-wider text-ink-400">東訊即時授權（API-6）</h4>
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    className="inline-flex items-center gap-1.5 border border-cream-300 bg-white rounded px-2.5 py-1 text-[11.5px] text-ink-600 hover:bg-cream-50 disabled:opacity-50"
                                                    onClick={testConnection}
                                                    disabled={testing}
                                                    title={`測試與 SDX500 ${row.api?.host || ''} 的 API 連線（登入+取 SIP 設定）`}
                                                >
                                                    {testing ? <Loader2 size={13} className="animate-spin" /> : <ServerCog size={13} />}
                                                    測試連線
                                                </button>
                                                <button
                                                    className="inline-flex items-center gap-1.5 border border-cream-300 bg-white rounded px-2.5 py-1 text-[11.5px] text-ink-600 hover:bg-cream-50 disabled:opacity-50"
                                                    onClick={refreshFromTecom}
                                                    disabled={refreshing}
                                                    title={`向 SDX500 ${row.api?.host || ''} 查授權`}
                                                >
                                                    {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                                    從東訊查授權
                                                </button>
                                            </div>
                                        </div>
                                        {connTest && (
                                            <div className={['mb-2 flex items-start gap-2 text-[12px] rounded-lg px-3 py-2 border',
                                                connTest.ok ? 'bg-success-50 text-success-700 border-success-500/20' : 'bg-danger-100/40 text-danger-600 border-danger-500/30'].join(' ')}>
                                                {connTest.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                                                {connTest.text}
                                            </div>
                                        )}
                                        <div className="bg-white border border-cream-200 rounded-lg p-4">
                                            {!remote && !remoteErr && (
                                                <div className="flex items-center gap-2 text-[12px] text-ink-400">
                                                    <ServerCog size={14} className="text-ink-300 shrink-0" />
                                                    按「從東訊查授權」向 SDX500（{row.api?.host || '—'}:{row.api?.port || '—'}）即時撈授權清單。
                                                </div>
                                            )}
                                            {remoteErr && (
                                                <div className="flex items-start gap-2 text-[12px] text-danger-600">
                                                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                                    <span>查詢失敗：{remoteErr}</span>
                                                </div>
                                            )}
                                            {remote && (
                                                <div className="space-y-2.5">
                                                    <div className="inline-flex items-center gap-1.5 text-[11.5px] font-medium px-2 py-0.5 rounded-full bg-success-50 text-success-700">
                                                        <CheckCircle2 size={13} />
                                                        {remote.synced ? '已從東訊同步（本地已更新）' : '已與東訊一致'}
                                                    </div>
                                                    <table className="w-full text-[12px]">
                                                        <thead>
                                                            <tr className="text-ink-400 text-[10px] uppercase tracking-wider">
                                                                <th className="text-left font-medium pb-1">ID</th>
                                                                <th className="text-left font-medium pb-1">類型</th>
                                                                <th className="text-left font-medium pb-1">席次</th>
                                                                <th className="text-left font-medium pb-1">期間</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(remote.remoteLicense || []).map((l, i) => (
                                                                <tr key={i} className="border-t border-cream-100 text-ink-700">
                                                                    <td className="py-1 font-mono">{l.Id}</td>
                                                                    <td className="py-1">{l.productType}</td>
                                                                    <td className="py-1">{l.seats} 席</td>
                                                                    <td className="py-1 font-mono text-[11px] text-ink-500">{fmtDate(l.licenseStart, row.region)} → {fmtDate(l.licenseEnd, row.region)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {remote.macId && <div className="text-[10.5px] text-ink-300 font-mono">SDX500 macId: {remote.macId}</div>}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        {/* API-4 / API-5 操作結果 */}
                        {opMsg && (
                            <div className={['mt-4 flex items-start gap-2 text-[12px] rounded-lg px-3 py-2 border',
                                opMsg.ok ? 'bg-success-50 text-success-700 border-success-500/20' : 'bg-danger-100/40 text-danger-600 border-danger-500/30'].join(' ')}>
                                {opMsg.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                                {opMsg.text}
                            </div>
                        )}
                        {/* 操作列：續約上傳（API-4）＋刪除 */}
                        <div className="mt-6 pt-4 border-t border-cream-200 flex items-center justify-between gap-3">
                            {canDelete ? (
                                <span className="text-[11.5px] text-ink-400">
                                    此授權尚未啟用（未建立客戶帳號），可直接刪除。
                                </span>
                            ) : (
                                <span className="text-[11.5px] text-ink-400">
                                    已啟用的授權無法直接刪除，需走拆除流程。
                                </span>
                            )}
                            <div className="flex items-center gap-2.5" onClick={(ev) => ev.stopPropagation()}>
                                {canMigrate && (
                                    <button
                                        type="button"
                                        onClick={migrateMac}
                                        disabled={busy}
                                        title="設備更換（換板 CCB → MAC 變）：把授權搬到新 MAC，帳號/租戶/角色/記錄全保留"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium text-ink-700 border border-cream-300 bg-white hover:bg-cream-50 transition disabled:opacity-50"
                                    >
                                        <RefreshCw size={14} /> 更換 MAC
                                    </button>
                                )}
                                {hasApi && (
                                    <label className={['inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium border transition cursor-pointer',
                                        busy ? 'opacity-50 pointer-events-none' : '',
                                        'text-accent-700 border-accent-500/40 bg-white hover:bg-accent-50'].join(' ')}
                                        title="上傳新 License.dat 通知東訊（API-4），成功後自動回查授權並同步席次"
                                    >
                                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                                        續約 / 加購（上傳 .dat）
                                        <input type="file" accept=".dat" className="hidden" onChange={onRenewPick} disabled={busy} />
                                    </label>
                                )}
                                {canDelete && (
                                    <button
                                        type="button"
                                        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium text-danger-500 border border-danger-500/30 bg-white hover:bg-danger-100/40 transition"
                                    >
                                        <Trash2 size={14} /> 刪除授權
                                    </button>
                                )}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

function todayISO(offsetYears = 0) {
    const d = new Date();
    d.setFullYear(d.getFullYear() + offsetYears);
    return d.toISOString().slice(0, 10);
}

function ImportDrawer({ dealers, onClose, onDone }) {
    const [mac, setMac] = useState('');
    const [distributorUid, setDistributorUid] = useState(dealers[0]?.uid || '');
    const [lines, setLines] = useState([
        { productType: 'AA', seats: 1, licenseStart: todayISO(), licenseEnd: todayISO(1), datFile: null },
    ]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    const macValid = /^[0-9a-fA-F]{12}$/.test(mac.replace(/[^0-9a-fA-F]/g, ''));

    async function onPickDatForLine(i, e) {
        const f = e.target.files?.[0];
        if (!f) return;
        const content = await f.text();
        setLine(i, { datFile: { filename: f.name, content } });
        e.target.value = '';
    }

    function setLine(i, patch) { setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l))); }
    function addLine() { setLines((ls) => [...ls, { productType: 'AA', seats: 1, licenseStart: todayISO(), licenseEnd: todayISO(1), datFile: null }]); }
    function rmLine(i) { setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls)); }

    async function submit() {
        setErr(null);
        if (!macValid) { setErr('MAC 格式須為 12 碼 hex（可含 : - 分隔）'); return; }
        if (!distributorUid) { setErr('請選擇經銷商'); return; }
        setSaving(true);
        try {
            await sdx500Api.preRegister({
                macAddress: mac,
                distributorUid,
                expectedLicenses: lines.map((l) => ({
                    productType: l.productType, seats: Number(l.seats) || 0,
                    licenseStart: l.licenseStart, licenseEnd: l.licenseEnd,
                })),
                datFiles: lines.filter((l) => l.datFile).map((l) => l.datFile),
            });
            onDone();
        } catch (e) {
            setErr(e.body?.hint || e.message);
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <div className="fixed inset-0 bg-ink-900/30 z-40" {...overlayDismiss(onClose)} />
            <aside className="fixed top-0 right-0 h-full w-[520px] max-w-[94vw] bg-cream-50 border-l border-cream-200 shadow-2xl z-50 flex flex-col">
                <header className="px-6 pt-6 pb-4 border-b border-cream-200">
                    <div className="inline-flex items-center gap-2 mb-2">
                        <span className="size-1.5 rounded-full bg-accent-500" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500">pre-register</span>
                    </div>
                    <h2 className="heading-display text-xl font-semibold text-ink-900">匯入授權</h2>
                    <p className="text-[12.5px] text-ink-500 mt-1.5">登錄一台 SDX500（MAC）與經銷商歸屬。送出後狀態為「待 SDX500 開通」。</p>
                </header>

                <div className="px-6 py-5 overflow-y-auto flex-1">
                    <Field label="MAC Address" hint="設備唯一主鍵，對應一個客戶 / 租戶">
                        <input
                            type="text"
                            value={mac}
                            onChange={(e) => setMac(e.target.value)}
                            placeholder="AA:BB:CC:DD:EE:FF"
                            className={[
                                'w-full px-3 py-2 text-sm font-mono tracking-wide bg-white border rounded-md focus:outline-none focus:ring-2',
                                mac && !macValid ? 'border-danger-500 focus:ring-danger-500/20' : 'border-cream-200 focus:border-accent-500 focus:ring-accent-500/20',
                            ].join(' ')}
                        />
                    </Field>

                    <Field label="經銷商歸屬" hint="客戶母帳號將掛在此經銷商底下（createdBy）">
                        <select
                            value={distributorUid}
                            onChange={(e) => setDistributorUid(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-white border border-cream-200 rounded-md focus:outline-none focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20"
                        >
                            {dealers.length === 0 && <option value="">（尚無經銷商）</option>}
                            {dealers.map((d) => (
                                <option key={d.uid} value={d.uid}>
                                    {(d.displayName || d.email)} — {d.email.replace('@tecom.com', '')}
                                </option>
                            ))}
                        </select>
                    </Field>

                    <Field label="預期授權（選填・核對用）" hint="期限預設 1 年；Id 由 SDX500 開通時帶入，這裡不填">
                        <div className="space-y-2.5">
                            {lines.map((l, i) => (
                                <div key={i} className="border border-cream-200 rounded-lg bg-white p-3.5">
                                    <div className="flex items-end gap-2.5">
                                        <div className="w-[88px]">
                                            <span className="block text-[10px] text-ink-400 font-mono uppercase mb-1.5">類型</span>
                                            <div className="flex border border-cream-300 rounded-md overflow-hidden">
                                                {['AA', 'CS'].map((pt) => (
                                                    <button
                                                        key={pt}
                                                        type="button"
                                                        onClick={() => setLine(i, { productType: pt })}
                                                        className={['flex-1 py-1.5 text-xs font-semibold font-mono', l.productType === pt ? 'bg-ink-900 text-cream-50' : 'bg-white text-ink-400'].join(' ')}
                                                    >
                                                        {pt}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex-1">
                                            <span className="block text-[10px] text-ink-400 font-mono uppercase mb-1.5">席次</span>
                                            <input type="number" min="1" value={l.seats} onChange={(e) => setLine(i, { seats: e.target.value })}
                                                className="w-full px-2 py-1.5 text-sm font-mono border border-cream-200 rounded-md focus:outline-none focus:border-accent-500" />
                                        </div>
                                        {lines.length > 1 && (
                                            <button type="button" onClick={() => rmLine(i)} className="text-ink-300 hover:text-danger-500 pb-1.5" title="移除">
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 gap-2.5 mt-3">
                                        <div>
                                            <span className="block text-[10px] text-ink-400 font-mono uppercase mb-1.5">起始</span>
                                            <input type="date" value={l.licenseStart} onChange={(e) => setLine(i, { licenseStart: e.target.value })}
                                                className="w-full px-2 py-1.5 text-[13px] border border-cream-200 rounded-md focus:outline-none focus:border-accent-500" />
                                        </div>
                                        <div>
                                            <span className="block text-[10px] text-ink-400 font-mono uppercase mb-1.5">終止</span>
                                            <input type="date" value={l.licenseEnd} onChange={(e) => setLine(i, { licenseEnd: e.target.value })}
                                                className="w-full px-2 py-1.5 text-[13px] border border-cream-200 rounded-md focus:outline-none focus:border-accent-500" />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-2.5">
                                        <span className="text-[10px] text-ink-400 font-mono uppercase mr-1">快速套用</span>
                                        {[1, 2, 3].map((y) => {
                                            const active = l.licenseStart && l.licenseEnd === bumpYears(l.licenseStart, y);
                                            return (
                                                <button
                                                    key={y}
                                                    type="button"
                                                    onClick={() => setLine(i, { licenseEnd: bumpYears(l.licenseStart || todayISO(), y) })}
                                                    className={[
                                                        'px-2.5 py-1 rounded-md text-[11.5px] border transition',
                                                        active ? 'bg-accent-500 text-white border-accent-500' : 'bg-white text-ink-700 border-cream-300 hover:border-accent-300 hover:text-accent-700',
                                                    ].join(' ')}
                                                >
                                                    {y} 年
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {/* 每張授權各自綁一張 .dat（檔名＝授權碼，續約用） */}
                                    <div className="mt-3 pt-3 border-t border-cream-100">
                                        {l.datFile ? (
                                            <div className="flex items-center gap-2">
                                                <FileCheck2 size={14} className="text-success-500 shrink-0" />
                                                <span className="flex-1 font-mono text-[11.5px] text-ink-700 truncate">{l.datFile.filename}</span>
                                                <button type="button" onClick={() => setLine(i, { datFile: null })} className="text-ink-300 hover:text-danger-500" title="移除"><X size={14} /></button>
                                            </div>
                                        ) : (
                                            <label className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-500 cursor-pointer hover:text-accent-700 transition">
                                                <Paperclip size={13} />
                                                附上授權檔 .dat（檔名＝授權碼，續約用）
                                                <input type="file" accept=".dat" className="hidden" onChange={(e) => onPickDatForLine(i, e)} />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addLine} className="w-full mt-2 border border-dashed border-cream-400 text-ink-500 text-[12.5px] py-2.5 rounded-md hover:border-accent-300 hover:text-accent-700 hover:bg-accent-50 transition">
                            ＋ 新增一筆授權（AA / CS）
                        </button>
                    </Field>

                    <div className="flex items-start gap-2.5 bg-warn-100 border border-[#e7cf9a] rounded-lg px-3.5 py-3 text-[12px] text-[#7a5512] leading-relaxed">
                        <Info size={15} className="mt-0.5 shrink-0" />
                        這是預登錄。SDX500 送來 provision 後，系統以 MAC 配對本筆、帶入分機/PBX、產生一次性客戶啟用連結。
                    </div>

                    {err && <div className="mt-3 text-[13px] text-danger-500">{err}</div>}
                </div>

                <footer className="px-6 py-4 border-t border-cream-200 flex gap-2.5 justify-end bg-cream-50">
                    <Button variant="outline" onClick={onClose} disabled={saving}>取消</Button>
                    <Button variant="accent" onClick={submit} disabled={saving}>
                        {saving ? <><Loader2 size={15} className="animate-spin" /> 送出中…</> : '送出預登錄'}
                    </Button>
                </footer>
            </aside>
        </>
    );
}

function bumpYears(dateStr, n) {
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    d.setFullYear(d.getFullYear() + n);
    return d.toISOString().slice(0, 10);
}

function Field({ label, hint, children }) {
    return (
        <div className="mb-5">
            <label className="block text-[12.5px] font-medium text-ink-700 mb-2">{label}</label>
            {children}
            {hint && <div className="text-[11.5px] text-ink-400 mt-1.5 font-mono">{hint}</div>}
        </div>
    );
}
